import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/auth/supabaseAuth';
import { useIsFetching } from '@tanstack/react-query';
import { toast } from 'sonner';

const WATCHDOG_TIMEOUT = 8000; // 8 seconds
const TOAST_THRESHOLD = 3000; // Show toast after 3s
const ERROR_THRESHOLD = 7000; // Show error after 7s

export const useRouteLoadingWatchdog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading: authLoading } = useSupabaseAuth();
  const isFetching = useIsFetching();
  const navigationStartTimeRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const toastShownRef = useRef(false);

  useEffect(() => {
    // Record navigation start time
    navigationStartTimeRef.current = Date.now();
    toastShownRef.current = false;

    // Clear any existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);

    // Show toast after 3 seconds if still loading
    toastTimeoutRef.current = setTimeout(() => {
      if (authLoading || isFetching > 0) {
        if (!toastShownRef.current) {
          toast.info('Still loading...', {
            description: 'Content is taking longer than usual',
            duration: 2000
          });
          toastShownRef.current = true;
        }
      }
    }, TOAST_THRESHOLD);

    // Show error toast after 7 seconds with escape hatch
    errorTimeoutRef.current = setTimeout(() => {
      if (authLoading || isFetching > 0) {
        toast.error('Loading is taking too long', {
          description: 'Click to return to dashboard',
          duration: 10000,
          action: {
            label: 'Go Back',
            onClick: () => navigate('/dashboard')
          }
        });
      }
    }, ERROR_THRESHOLD);

    // Console warning after 8 seconds
    timeoutRef.current = setTimeout(() => {
      const elapsed = Date.now() - navigationStartTimeRef.current;
      
      if (authLoading || isFetching > 0) {
        console.warn(
          `🐕 Route Loading Watchdog Alert:\n` +
          `Route: ${location.pathname}\n` +
          `Elapsed: ${elapsed}ms\n` +
          `Auth Loading: ${authLoading}\n` +
          `Active Queries: ${isFetching}\n` +
          `⚠️ Navigation is taking longer than expected!`
        );
      }
    }, WATCHDOG_TIMEOUT);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    };
  }, [location.pathname, authLoading, isFetching, navigate]);

  // Log successful fast navigations
  useEffect(() => {
    if (!authLoading && isFetching === 0 && navigationStartTimeRef.current > 0) {
      const elapsed = Date.now() - navigationStartTimeRef.current;
      if (elapsed < 1000) {
        console.log(`✅ Fast navigation to ${location.pathname} (${elapsed}ms)`);
      }
    }
  }, [authLoading, isFetching, location.pathname]);
};
